import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Award, AlignLeft } from 'lucide-react';

const MatchRecapModal = ({ isOpen, onClose, game, players, onSave }) => {
    const [matchRecap, setMatchRecap] = useState('');
    const [performances, setPerformances] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize from game data
    useEffect(() => {
        if (isOpen && game) {
            setMatchRecap(game.matchRecap || '');
            // Ensure performances exists and clone it to avoid mutating original
            setPerformances(game.memorablePerformances ? JSON.parse(JSON.stringify(game.memorablePerformances)) : []);
        }
    }, [isOpen, game]);

    // Only show players who were part of the playing squad (playing11 + superSubs)
    const availablePlayers = React.useMemo(() => {
        if (!players || !game) return [];
        
        if (game.playing11 && game.playing11.length > 0) {
            const squadIds = new Set([
                ...(game.playing11 || []),
                ...(game.superSubs || [])
            ]);
            return players.filter(p => squadIds.has(p.id) || squadIds.has(p.userId));
        }
        
        return players;
    }, [players, game]);

    if (!isOpen) return null;

    const handleAddPerformance = () => {
        setPerformances([...performances, { playerIds: [], description: '' }]);
    };

    const handleRemovePerformance = (index) => {
        const updated = [...performances];
        updated.splice(index, 1);
        setPerformances(updated);
    };

    const handlePerformanceChange = (index, field, value) => {
        const updated = [...performances];
        updated[index][field] = value;
        setPerformances(updated);
    };

    const handleTogglePlayer = (perfIndex, playerId) => {
        const updated = [...performances];
        const perfPlayers = updated[perfIndex].playerIds || [];

        if (perfPlayers.includes(playerId)) {
            updated[perfIndex].playerIds = perfPlayers.filter(id => id !== playerId);
        } else {
            updated[perfIndex].playerIds = [...perfPlayers, playerId];
        }

        setPerformances(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            // Filter out empty performances
            const validPerformances = performances.filter(
                p => p.description.trim() !== '' && p.playerIds && p.playerIds.length > 0
            );

            await onSave({
                matchRecap: matchRecap.trim(),
                memorablePerformances: validPerformances
            });

            onClose();
        } catch (error) {
            console.error('Error saving match recap:', error);
            alert('Failed to save recap. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <AlignLeft className="text-blue-600" />
                            Match Recap & Highlights
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Summarize {game.name} and log memorable performances.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Form Body - Scrollable */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">

                    {/* Section 1: Overall Summary */}
                    <div className="space-y-3">
                        <label className="block text-sm font-bold text-gray-700">
                            Match Summary
                        </label>
                        <textarea
                            value={matchRecap}
                            onChange={(e) => setMatchRecap(e.target.value)}
                            placeholder="Write a brief summary of how the match went..."
                            rows={4}
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-y"
                        />
                    </div>

                    <hr className="border-gray-100" />

                    {/* Section 2: Memorable Performances */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <label className="block text-sm font-bold text-gray-700 flex items-center gap-2">
                                <Award className="text-yellow-500" size={18} />
                                Memorable Performances
                            </label>
                            <button
                                type="button"
                                onClick={handleAddPerformance}
                                className="text-sm text-blue-600 font-semibold flex items-center gap-1 hover:text-blue-800"
                            >
                                <Plus size={16} /> Add Highlight
                            </button>
                        </div>

                        {performances.length === 0 ? (
                            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <Award className="mx-auto text-gray-400 mb-2" size={24} />
                                <p className="text-sm text-gray-500">No memorable performances added yet.</p>
                                <button
                                    type="button"
                                    onClick={handleAddPerformance}
                                    className="mt-2 text-sm text-blue-600 font-semibold"
                                >
                                    Click here to add one
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {performances.map((perf, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative">
                                        <button
                                            onClick={() => handleRemovePerformance(index)}
                                            className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition-colors p-1"
                                            title="Remove highlight"
                                        >
                                            <Trash2 size={16} />
                                        </button>

                                        <div className="space-y-4 pr-6">
                                            {/* Player Selection */}
                                            <div>
                                                <span className="block text-xs font-semibold text-gray-600 mb-2">Players Involved</span>
                                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1 bg-white border border-gray-200 rounded-lg w-full">
                                                    {availablePlayers.length > 0 ? (
                                                        availablePlayers.map(player => {
                                                            const isSelected = (perf.playerIds || []).includes(player.id || player.userId);
                                                            return (
                                                                <button
                                                                    key={player.id || player.userId}
                                                                    type="button"
                                                                    onClick={() => handleTogglePlayer(index, player.id || player.userId)}
                                                                    className={`px-2 py-1 text-xs font-medium rounded-full transition-colors border ${isSelected
                                                                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                                                                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                                                                        }`}
                                                                >
                                                                    {player.name}
                                                                </button>
                                                            );
                                                        })
                                                    ) : (
                                                        <span className="text-xs text-gray-500 p-2">No squad members available.</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Description Input */}
                                            <div>
                                                <span className="block text-xs font-semibold text-gray-600 mb-1">Achievement Description</span>
                                                <input
                                                    type="text"
                                                    value={perf.description}
                                                    onChange={(e) => handlePerformanceChange(index, 'description', e.target.value)}
                                                    placeholder="e.g. Scored 50 off 20 balls, 5-wicket haul, etc."
                                                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? 'Saving...' : 'Save Recap'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default MatchRecapModal;
